import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Wand2,
  Crown,
  Loader2,
  Sparkles,
  Clock,
  Bookmark,
  BookmarkCheck,
  Share2,
  Keyboard,
  Dna,
  ShoppingBag,
  Lock,
  X,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { dupeSearch } from "../lib/gemini";
// Dupe Finder embedding + candidate retrieval + Gemini ranking + result
// caching all run server-side in the `gemini-proxy` Edge Function.
import { useShareSheet } from "../components/ShareSheet";
import { toast } from "sonner";

const TIERS = ["All", "Budget", "Mid-range", "Luxury"];
const SUGGESTED = [
  "Charlotte Tilbury Flawless Filter",
  "Drunk Elephant C-Firma",
  "La Mer Moisturising Cream",
];

const RECENT_KEY = "glow-dupes-recent";

// Rotating loader messages — cycles every 3s while searching.
const LOADER_STEPS = [
  "Analysing ingredients...",
  "Matching your skin profile...",
  "Ranking by savings...",
  "Brewing your dupes...",
];

// Image background palette by inferred category keywords in the product name.
function imgBgFor(name = "", category = "") {
  const t = `${name} ${category}`.toLowerCase();
  if (t.match(/moistur|cream|lotion|balm/)) return "#FDE8F0"; // pink
  if (t.match(/serum|essence|ampoule/)) return "#E8F0FD"; // blue
  if (t.match(/toner|mist|water/)) return "#E8FDF0"; // green
  if (t.match(/oil|elixir/)) return "#FDF5E8"; // gold
  return "#F5E8FD"; // default soft purple
}

function emojiFor(name = "", category = "") {
  const t = `${name} ${category}`.toLowerCase();
  if (t.match(/serum|essence|ampoule/)) return "💧";
  if (t.match(/oil|elixir/)) return "🫒";
  if (t.match(/toner|mist|water/)) return "🌿";
  if (t.match(/spf|sunscreen/)) return "☀️";
  if (t.match(/mask|peel/)) return "✨";
  return "🧴";
}

export default function Dupes() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const share = useShareSheet();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState("search"); // search | saved
  const [query, setQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [tier, setTier] = useState("All");
  const [busy, setBusy] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [fromCache, setFromCache] = useState(false);
  const [recent, setRecent] = useState([]);
  const [savedRows, setSavedRows] = useState([]); // full rows w/ payload
  const isPremium = !!profile?.is_premium;

  const savedIds = useMemo(
    () => new Set(savedRows.map((r) => r.content_id)),
    [savedRows]
  );

  useEffect(() => {
    const raw = localStorage.getItem(RECENT_KEY);
    if (raw) {
      try {
        setRecent(JSON.parse(raw).slice(0, 3));
      } catch {
        /* ignore corrupt localStorage */
      }
    }
  }, []);

  // Rotating loader messages — cycles every 3s while searching.
  useEffect(() => {
    if (!busy) return;
    setLoadingStep(0);
    const id = setInterval(() => {
      setLoadingStep((s) => (s + 1) % LOADER_STEPS.length);
    }, 3000);
    return () => clearInterval(id);
  }, [busy]);

  // Load already-saved dupes (full payload) so the bookmark icon shows correct state
  // and the Saved tab can render rich cards.
  useEffect(() => {
    if (!profile?.id) return;
    supabase
      .from("saved_content")
      .select("content_id, payload, saved_at")
      .eq("user_id", profile.id)
      .eq("content_type", "dupe")
      .order("saved_at", { ascending: false })
      .then(({ data }) => setSavedRows(data || []));
  }, [profile?.id]);

  // Auto-populate from ?q= (used by ProductChips deep-links).
  useEffect(() => {
    const q = searchParams.get("q");
    if (q && !activeQuery && isPremium) {
      setQuery(q);
      // Fire after layout so the search input is mounted.
      requestAnimationFrame(() => runSearch(q));
      // Clear param to keep URL clean.
      searchParams.delete("q");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isPremium]);

  const pushRecent = (q) => {
    const next = [q, ...recent.filter((x) => x.toLowerCase() !== q.toLowerCase())].slice(0, 3);
    setRecent(next);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  };

  // Sanitize user input — strip control chars, normalise whitespace, cap length.
  // The dupe finder feeds this directly into a Gemini prompt + a Supabase
  // cache key, so we need it to be plain text without injection vectors.
  const sanitizeQuery = (raw) => {
    if (typeof raw !== "string") return "";
    return raw
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001f\u007f]/g, " ") // control chars
      .replace(/[<>{}]/g, " ")                // tag-ish / brace chars
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 120);
  };

  const runSearch = async (rawQ) => {
    const q = sanitizeQuery(rawQ);
    if (!q || !isPremium) return;
    setBusy(true);
    setError("");
    setResults(null);
    setFromCache(false);
    setActiveQuery(q);
    pushRecent(q);

    // ALL heavy lifting (cache check, DB candidate retrieval, Gemini call,
    // cache write) now happens in the `gemini-proxy` Edge Function via
    // mode="dupe_search". Frontend just sends the query + tier + profile.
    // Client-side 25s guard so gibberish/unresolvable searches don't spin
    // forever — surfaces a friendly no-match state.
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("timeout")), 25000)
    );
    try {
      const { dupes, fromCache: cached } = await Promise.race([
        dupeSearch({
          query: q,
          tier,
          profile: {
            skin_type: profile.skin_type,
            skin_tone: profile.skin_tone,
            hair_type: profile.hair_type,
            concerns: profile.concerns || [],
          },
        }),
        timeout,
      ]);
      if (!dupes || dupes.length === 0) {
        setError(
          `We couldn't find a strong match for "${q}". Try the full brand + product name, or a different product.`
        );
      } else {
        setResults(dupes.slice(0, 3));
        setFromCache(!!cached);
      }
    } catch (e) {
      const msg = e?.message === "timeout"
        ? `That search is taking longer than expected. Try a more specific product name (brand + product).`
        : e?.message || "Couldn't find dupes right now. Try again in a moment.";
      setError(msg);
      console.error("[dupes]", e);
    }
    setBusy(false);
  };

  const handleFind = () => runSearch(query);

  const handleShare = (d) => {
    const savingsMatch = (d.savingsNote || "").match(/\$\d+(\.\d+)?/);
    share.open({
      type: "dupe",
      payload: {
        productName: activeQuery,
        matchPercent: d.matchPercent,
        savings: savingsMatch ? savingsMatch[0] : null,
      },
    });
  };

  const handleSave = async (d) => {
    if (!profile?.id) return;
    const cid = `dupe:${d.brand}:${d.name}`;
    if (savedIds.has(cid)) {
      const { error } = await supabase
        .from("saved_content")
        .delete()
        .eq("user_id", profile.id)
        .eq("content_id", cid);
      if (error) {
        toast.error("Could not remove. Try again.");
        return;
      }
      setSavedRows((rows) => rows.filter((r) => r.content_id !== cid));
      toast("Removed from your dupes");
    } else {
      const payload = {
        brand: d.brand,
        name: d.name,
        price: d.price,
        tier: d.tier,
        matchPercent: d.matchPercent,
        savingsNote: d.savingsNote,
        whyItWorks: d.whyItWorks,
        category: d.category,
        searchedFor: activeQuery,
      };
      const { data, error } = await supabase
        .from("saved_content")
        .insert({
          user_id: profile.id,
          content_id: cid,
          content_type: "dupe",
          payload,
        })
        .select()
        .maybeSingle();
      if (error) {
        toast.error(`Could not save: ${error.message}`);
        return;
      }
      setSavedRows((rows) => [data, ...rows]);
      toast.success("Saved to your dupes");
    }
  };

  return (
    <div className="-mt-px" data-testid="dupes-page">
      {/* PINK HEADER */}
      <div
        className="relative pt-5 px-5 pb-5"
        style={{ background: "linear-gradient(180deg, #C9748A 0%, #B8657A 100%)" }}
        data-testid="dupes-header"
      >
        <div className="flex items-start justify-between mb-2">
          <div className="min-w-0">
            <h1 className="font-serif text-white text-3xl leading-tight">
              Dupe Finder
            </h1>
            <p className="text-white/85 text-sm font-light mt-0.5">
              Find affordable swaps for any product
            </p>
          </div>
          {isPremium && (
            <div
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-wider font-medium text-white shrink-0 ml-2"
              style={{ background: "linear-gradient(135deg, #C9975A 0%, #E3BA85 100%)" }}
              data-testid="dupes-premium-badge"
            >
              <Crown size={11} /> Premium
            </div>
          )}
        </div>

        {/* Profile tags */}
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="dupes-profile-tags">
          {[profile?.skin_type, profile?.skin_tone, profile?.hair_type]
            .filter(Boolean)
            .map((t) => (
              <span
                key={t}
                className="text-[10px] uppercase tracking-wider bg-white/20 text-white px-2.5 py-1 rounded-full font-medium"
              >
                {t}
              </span>
            ))}
        </div>

        {/* Search bar */}
        <div
          className="bg-white rounded-full p-1.5 pl-4 flex items-center gap-2"
          data-testid="dupes-searchbar"
        >
          <Search size={16} className="text-cocoa/40 shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. CeraVe Moisturising Cream"
            onKeyDown={(e) => e.key === "Enter" && handleFind()}
            disabled={!isPremium}
            data-testid="dupes-search-input"
            className="flex-1 min-w-0 bg-transparent text-sm focus:outline-none placeholder:text-cocoa/40 text-cocoa py-2"
          />
          <button
            onClick={handleFind}
            disabled={busy || !query.trim() || !isPremium}
            data-testid="dupes-find-btn"
            className="rounded-full bg-rose text-white px-4 py-2 text-xs font-medium disabled:opacity-40 flex items-center gap-1 shrink-0"
          >
            {busy ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <>
                Find <span>→</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* CONTENT */}
      <div className="px-5 pt-4 pb-24 relative">
        {/* Search / Saved tab switcher */}
        <div className="flex gap-2 mb-4" data-testid="dupes-tabs">
          <button
            onClick={() => setView("search")}
            data-testid="dupes-tab-search"
            className={`flex-1 rounded-full py-2 text-sm font-medium transition-all ${
              view === "search"
                ? "bg-rose text-white shadow-rose"
                : "bg-white text-cocoa/60 border border-[#f0e8e4]"
            }`}
          >
            Search
          </button>
          <button
            onClick={() => setView("saved")}
            data-testid="dupes-tab-saved"
            className={`flex-1 rounded-full py-2 text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
              view === "saved"
                ? "bg-rose text-white shadow-rose"
                : "bg-white text-cocoa/60 border border-[#f0e8e4]"
            }`}
          >
            <Bookmark size={12} className={view === "saved" ? "fill-white" : ""} />
            Saved
            {savedRows.length > 0 && (
              <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${view === "saved" ? "bg-white/25" : "bg-rose/10 text-rose"}`}>
                {savedRows.length}
              </span>
            )}
          </button>
        </div>

        {/* SAVED VIEW */}
        {view === "saved" && (
          <SavedView
            rows={savedRows}
            onRemove={handleSave}
            onShare={(p) =>
              share.open({
                type: "dupe",
                payload: {
                  productName: p.searchedFor || p.name,
                  matchPercent: p.matchPercent,
                  savings: (p.savingsNote || "").match(/\$\d+(\.\d+)?/)?.[0] || null,
                },
              })
            }
          />
        )}

        {view === "search" && (
          <>
        {/* How it works */}
        <div
          className="rounded-2xl p-4 mb-4"
          style={{ background: "#FDE8F0" }}
          data-testid="how-it-works"
        >
          <p className="text-[10px] uppercase tracking-[0.22em] font-medium text-rose mb-3 text-center">
            How it works
          </p>
          <div className="flex items-center justify-between gap-1">
            <HIWStep icon={Keyboard} label="Type full product name" />
            <span className="text-rose text-base">›</span>
            <HIWStep icon={Dna} label="Dupe Finder matches ingredients" />
            <span className="text-rose text-base">›</span>
            <HIWStep icon={Sparkles} label="Get top 3 dupes for you" />
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar -mx-1 px-1">
          {TIERS.map((t) => (
            <button
              key={t}
              onClick={() => setTier(t)}
              data-testid={`dupes-tier-${t.toLowerCase()}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                tier === t
                  ? "bg-rose text-white shadow-rose"
                  : "bg-white text-cocoa/60 border border-[#f0e8e4]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Premium gate */}
        {!isPremium && (
          <PremiumGate
            onUpgrade={() => navigate("/app/premium")}
          />
        )}

        {/* Results */}
        {isPremium && (
          <>
            {error && (
              <p
                className="text-sm text-rose/80 mb-3 px-2 text-center"
                data-testid="dupes-error"
              >
                {error}
              </p>
            )}

            {busy && (
              <div
                className="flex flex-col items-center justify-center py-10"
                data-testid="dupes-loading"
              >
                <Loader2 size={28} className="text-rose animate-spin mb-3" />
                <AnimatePresence mode="wait">
                  <motion.p
                    key={loadingStep}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.35 }}
                    className="text-sm font-light text-cocoa/70"
                    data-testid="dupes-loading-message"
                  >
                    {LOADER_STEPS[loadingStep]}
                  </motion.p>
                </AnimatePresence>
              </div>
            )}

            {results && !busy && (
              <div data-testid="dupes-results">
                <div className="flex items-baseline justify-between mb-3 px-1">
                  <h2
                    className="font-serif text-xl leading-tight"
                    style={{ color: "#2E1608" }}
                  >
                    Top Dupes for {activeQuery.split(" ").slice(0, 2).join(" ")}
                  </h2>
                  <span className="text-[11px] text-cocoa/55">
                    {results.length} match{results.length === 1 ? "" : "es"}
                    {fromCache && " · cached"}
                  </span>
                </div>
                <div className="space-y-4">
                  {results.map((d, i) => (
                    <motion.div
                      key={`${d.brand}-${d.name}-${i}`}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.12 }}
                    >
                      <DupeCard
                        dupe={d}
                        index={i}
                        searchedFor={activeQuery}
                        saved={savedIds.has(`dupe:${d.brand}:${d.name}`)}
                        onShare={() => handleShare(d)}
                        onSave={() => handleSave(d)}
                      />
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {!results && !busy && !error && (
              <div data-testid="dupes-empty" className="text-center py-6">
                <div className="text-5xl mb-3">🔍</div>
                <h2
                  className="font-serif text-xl mb-1"
                  style={{ color: "#2E1608" }}
                >
                  Search any beauty product to find your perfect dupe
                </h2>
                <p className="text-xs text-cocoa/55 font-light mb-4">
                  Type the full product name for best results
                </p>
                <p className="text-[10px] uppercase tracking-wider text-cocoa/45 mb-2">
                  Popular searches
                </p>
                <div className="flex flex-col items-center gap-2">
                  {SUGGESTED.map((s) => (
                    <button
                      key={s}
                      onClick={() => {
                        setQuery(s);
                        runSearch(s);
                      }}
                      data-testid={`suggested-${s.split(" ")[0].toLowerCase()}`}
                      className="rounded-full bg-white border border-[#f0e8e4] px-4 py-2 text-xs font-medium text-cocoa hover:border-rose hover:text-rose transition-all"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Recent searches */}
            {recent.length > 0 && (
              <div className="mt-6" data-testid="recent-searches">
                <h3
                  className="font-serif text-lg mb-2"
                  style={{ color: "#2E1608" }}
                >
                  Recent Searches
                </h3>
                <div className="flex flex-col gap-2">
                  {recent.map((r, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setQuery(r);
                        runSearch(r);
                      }}
                      data-testid={`recent-${i}`}
                      className="flex items-center gap-2 rounded-full bg-white border border-[#f0e8e4] px-4 py-2 text-xs font-light text-cocoa text-left"
                    >
                      <Clock size={12} className="text-cocoa/45" />
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
          </>
        )}
      </div>
    </div>
  );
}

function HIWStep({ icon: Icon, label }) {
  return (
    <div className="flex-1 flex flex-col items-center text-center min-w-0">
      <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center mb-1.5">
        <Icon size={16} className="text-rose" strokeWidth={1.6} />
      </div>
      <p className="text-[10px] text-rose font-light leading-tight px-0.5">
        {label}
      </p>
    </div>
  );
}

function DupeCard({ dupe, index, searchedFor, saved, onShare, onSave }) {
  const tierColors = {
    Budget: { bg: "#E8FDF0", color: "#1A5C35" },
    "Mid-range": { bg: "#F5E8FD", color: "#6B3FA0" },
    Luxury: { bg: "#FDF5E8", color: "#7A4A0A" },
  };
  const tone = tierColors[dupe.tier] || tierColors.Luxury;
  const shopHref = dupe.shopUrl ||
    `https://www.google.com/search?q=${encodeURIComponent(`${dupe.brand} ${dupe.name} buy`)}`;

  return (
    <div
      className="bg-white rounded-2xl p-4 border border-[#f0e8e4]"
      data-testid={`dupe-card-${index}`}
    >
      {/* Top row: image + title block */}
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-16 h-16 rounded-xl flex flex-col items-center justify-center text-2xl shrink-0 relative overflow-hidden"
          style={{ background: imgBgFor(dupe.name, dupe.category) }}
        >
          <span>{emojiFor(dupe.name, dupe.category)}</span>
          <span
            className="absolute bottom-0 inset-x-0 text-[9px] text-white font-medium text-center py-0.5"
            style={{ background: "rgba(46,22,8,0.55)" }}
          >
            {dupe.brand?.split(" ")[0]?.slice(0, 10)}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-[11px] text-cocoa/55 truncate">{dupe.brand}</p>
            {dupe.topPick ? (
              <span
                className="text-[10px] uppercase tracking-wider text-white px-2 py-0.5 rounded-full shrink-0 inline-flex items-center gap-1"
                style={{ background: "linear-gradient(135deg, #C9975A 0%, #E3BA85 100%)" }}
                data-testid={`top-pick-${index}`}
              >
                <Crown size={9} /> Top pick
              </span>
            ) : (
              <span
                className="text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0"
                style={{ background: tone.bg, color: tone.color }}
              >
                {dupe.price} · {dupe.tier}
              </span>
            )}
          </div>
          <h3
            className="font-serif text-lg leading-tight mt-0.5"
            style={{ color: "#2E1608" }}
          >
            {dupe.name}
          </h3>
          {dupe.topPick && (
            <span
              className="inline-block mt-1 text-[10px] font-medium px-2 py-0.5 rounded-full"
              style={{ background: tone.bg, color: tone.color }}
            >
              {dupe.price} · {dupe.tier}
            </span>
          )}
        </div>
      </div>

      {/* Match bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] uppercase tracking-wider text-cocoa/55 font-medium">
            Match
          </span>
          <span className="text-xs font-medium text-rose">{dupe.matchPercent}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-rose/10 overflow-hidden">
          <div
            className="h-full"
            style={{
              width: `${dupe.matchPercent}%`,
              background: "linear-gradient(90deg, #C9748A 0%, #C9975A 100%)",
            }}
          />
        </div>
      </div>

      {/* Why it works */}
      <p
        className="text-sm font-light leading-relaxed mb-3"
        style={{ color: "#2E1608" }}
        dangerouslySetInnerHTML={{
          __html: (dupe.whyItWorks || "").replace(
            /\*\*(.+?)\*\*/g,
            '<strong class="font-semibold">$1</strong>'
          ),
        }}
      />

      {/* Ingredient chips */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {(dupe.ingredients || []).map((ing) => (
          <span
            key={ing}
            className="text-[10px] bg-cream text-cocoa/70 px-2 py-1 rounded-full"
          >
            {ing}
          </span>
        ))}
      </div>

      {/* Safety flags */}
      {(dupe.safetyFlags || []).length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3">
          {dupe.safetyFlags.map((f) => (
            <span
              key={f}
              className="text-[10px] uppercase tracking-wider font-medium text-rose"
            >
              ✓ {f}
            </span>
          ))}
        </div>
      )}

      {/* Savings */}
      {dupe.savingsNote && (
        <div
          className="rounded-lg px-3 py-2 mb-3 text-xs font-light"
          style={{ background: "#E8FDF0", color: "#1A5C35" }}
        >
          💰 {dupe.savingsNote}
        </div>
      )}

      {/* Shop Now */}
      <a
        href={shopHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`shop-${index}`}
        className="block w-full rounded-full py-2.5 text-center text-sm font-medium text-white shadow-rose"
        style={{ background: "#C9748A" }}
      >
        Shop Now →
      </a>

      {/* Share + Save */}
      <div className="grid grid-cols-2 gap-2 mt-2">
        <button
          onClick={onShare}
          data-testid={`share-${index}`}
          className="rounded-full py-2 text-xs font-medium bg-white border border-[#f0e8e4] text-rose flex items-center justify-center gap-1.5"
        >
          <Share2 size={12} /> Share my dupe
        </button>
        <button
          onClick={onSave}
          data-testid={`save-${index}`}
          className="rounded-full py-2 text-xs font-medium bg-white border border-[#f0e8e4] text-rose flex items-center justify-center gap-1.5"
        >
          {saved ? <BookmarkCheck size={12} /> : <Bookmark size={12} />}{" "}
          {saved ? "Saved" : "Save"}
        </button>
      </div>
    </div>
  );
}

function PremiumGate({ onUpgrade }) {
  return (
    <div
      className="bg-white rounded-2xl p-6 border border-[#f0e8e4] text-center mb-4"
      data-testid="dupes-premium-gate"
    >
      <Crown
        size={28}
        className="mx-auto mb-2"
        style={{ color: "#C9975A" }}
      />
      <h3
        className="font-serif text-xl mb-1"
        style={{ color: "#2E1608" }}
      >
        Unlock Dupe Finder with Premium
      </h3>
      <p className="text-xs text-cocoa/55 font-light mb-4 max-w-[260px] mx-auto">
        Search any product and get dupes tailored to your skin tone,
        hair texture and concerns.
      </p>
      <button
        onClick={onUpgrade}
        data-testid="dupes-upgrade-btn"
        className="rounded-full px-6 py-2.5 text-sm font-medium text-white shadow-rose"
        style={{ background: "#C9748A" }}
      >
        Upgrade
      </button>

      {/* Decorative blurred placeholder cards */}
      <div className="mt-6 space-y-3 select-none pointer-events-none blur-sm opacity-60">
        {[0, 1].map((i) => (
          <div
            key={i}
            className="bg-cream rounded-2xl p-4 border border-[#f0e8e4] text-left"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-16 h-16 rounded-xl bg-rose/15" />
              <div className="flex-1 space-y-1.5">
                <div className="h-2 w-1/3 bg-rose/15 rounded-full" />
                <div className="h-3 w-3/4 bg-cocoa/15 rounded-full" />
              </div>
            </div>
            <div className="h-1.5 bg-rose/15 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

function SavedView({ rows, onRemove, onShare }) {
  if (rows.length === 0) {
    return (
      <div
        className="bg-white rounded-2xl p-8 border border-[#f0e8e4] text-center"
        data-testid="saved-empty"
      >
        <Bookmark size={28} className="mx-auto mb-2 text-rose" />
        <h3 className="font-serif text-xl mb-1" style={{ color: "#2E1608" }}>
          No saved dupes yet
        </h3>
        <p className="text-xs text-cocoa/55 font-light">
          Tap the Save button on any dupe to bookmark it here.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="saved-list">
      {rows.map((row) => (
        <SavedDupeCard
          key={row.content_id}
          row={row}
          onRemove={() => onRemove({ brand: row.payload?.brand, name: row.payload?.name })}
          onShare={() => onShare(row.payload || {})}
        />
      ))}
    </div>
  );
}

function SavedDupeCard({ row, onRemove, onShare }) {
  const p = row.payload || {};
  const tier = (p.tier || "").toLowerCase();
  const tone =
    { budget: { bg: "#E8FDF0", color: "#1A5C35" }, "mid-range": { bg: "#F5E8FD", color: "#6B3FA0" }, luxury: { bg: "#FDF5E8", color: "#7A4A0A" } }[tier] || { bg: "#FDE8F0", color: "#C9748A" };
  return (
    <div
      className="bg-white rounded-2xl p-4 border border-[#f0e8e4]"
      data-testid={`saved-card-${p.brand}`}
    >
      <div className="flex items-start gap-3 mb-2">
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center text-xl shrink-0"
          style={{ background: "#FDE8F0" }}
        >
          🧴
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-[11px] text-cocoa/55 truncate">{p.brand}</p>
            {p.tier && (
              <span
                className="text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0"
                style={{ background: tone.bg, color: tone.color }}
              >
                {p.price} · {p.tier}
              </span>
            )}
          </div>
          <h3
            className="font-serif text-base leading-tight mt-0.5"
            style={{ color: "#2E1608" }}
          >
            {p.name}
          </h3>
          {p.searchedFor && (
            <p className="text-[10px] text-cocoa/45 mt-0.5">
              Dupe of {p.searchedFor}
            </p>
          )}
        </div>
      </div>
      {typeof p.matchPercent === "number" && (
        <div className="mb-2">
          <div className="flex items-center justify-between text-[10px] text-cocoa/55 mb-1">
            <span className="uppercase tracking-wider">Match</span>
            <span className="text-rose font-medium">{p.matchPercent}%</span>
          </div>
          <div className="h-1 rounded-full bg-rose/10 overflow-hidden">
            <div
              className="h-full"
              style={{
                width: `${p.matchPercent}%`,
                background: "linear-gradient(90deg, #C9748A 0%, #C9975A 100%)",
              }}
            />
          </div>
        </div>
      )}
      {p.savingsNote && (
        <p
          className="text-[11px] rounded-lg px-2.5 py-1.5 mb-2 font-light"
          style={{ background: "#E8FDF0", color: "#1A5C35" }}
        >
          💰 {p.savingsNote}
        </p>
      )}
      <div className="grid grid-cols-2 gap-2 mt-2">
        <button
          onClick={onShare}
          data-testid={`saved-share-${p.brand}`}
          className="rounded-full py-2 text-xs font-medium bg-white border border-[#f0e8e4] text-rose flex items-center justify-center gap-1"
        >
          <Share2 size={11} /> Share
        </button>
        <button
          onClick={onRemove}
          data-testid={`saved-remove-${p.brand}`}
          className="rounded-full py-2 text-xs font-medium bg-white border border-[#f0e8e4] text-cocoa/65"
        >
          Remove
        </button>
      </div>
    </div>
  );
}

